// VideoCallModal.jsx — KweryAI styled, powered by LiveKit
// ─────────────────────────────────────────────────────────────
// In-call UI redesign (design_specs/kwery-call-alternates.html). Two layout
// variants render over the SAME LiveKit room / tracks — the toggle only swaps
// the visual arrangement, never the media session (no reconnect on toggle):
//
//   • Presence  — ambient people-first view: pulsing avatar "orbs" (audio) or a
//                 focused speaker + participant row (video).
//   • Workspace — people as a left rail, the shared work (screen share / active
//                 speaker) as the main stage.
//
// The chosen variant is persisted per-user in localStorage
// (kwery.callui.variant.<userId>, default: presence) — same read/validate/write
// idiom as RightRail's collapsible-card persistence.
//
// All call logic is preserved: token flow, track subscription, the
// 2nd-participant timer start (→ onAnswered, which clears the caller's ringing
// card), mute/camera/screen-share, recording (incl. the consent notice pushed to
// CallContext), in-call chat, and the end/close flow. External contract
// (props + callbacks) is unchanged, so CallContext.jsx needs no changes.
// ─────────────────────────────────────────────────────────────
import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  ParticipantTile,
  useTracks,
  useChat,
  useParticipants,
  useLocalParticipant,
  useDataChannel,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";
import {
  Mic, MicOff, Video, VideoOff, ScreenShare, ScreenShareOff,
  Circle, Square, MessageSquare, StickyNote, PenLine, Eraser, Trash2,
  PhoneOff, Radio, PanelLeft,
  Loader2, AlertTriangle, Users, Send, Smile, Tag, X,
  ChevronLeft, ChevronRight,
} from "lucide-react";

// Max tiles per page in any paged grid/rail before prev/next kicks in.
const GRID_PAGE_SIZE = 12;

const NEON     = "#00FF99";
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const LK_URL   = process.env.REACT_APP_LIVEKIT_URL || "ws://localhost:7880";

const HEAD = { fontFamily: "'Space Grotesk', system-ui, sans-serif" };
const BODY = "'Inter', system-ui, sans-serif";

const TAG_COLORS = {
  urgent:"#ef4444", important:"#facc15", todo:"#60a5fa", checkin:"#a78bfa", fyi:"#94a3b8",
};
const TAGS   = ["urgent","important","todo","checkin","fyi"];
const EMOJIS = ["👍","👎","😄","😮","❤️","🎉","🔥","✅","⚠️","📌","💡","🙏"];
const fmt    = s => `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;

const initials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
};

// A camera trackRef only carries real video when it has a live, unmuted
// publication; withPlaceholder refs (camera off / audio-only) have none.
const hasLiveVideo = (tr) =>
  !!tr && tr.source === Track.Source.Camera && !!tr.publication && !tr.publication.isMuted;

// Measure a container so the grid can size equal 16:9 tiles that fit exactly.
function useElementSize() {
  const ref = React.useRef(null);
  const [size, setSize] = React.useState({ width: 0, height: 0 });
  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0].contentRect;
      setSize({ width: cr.width, height: cr.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

// prev / page x/y / next — shared by the presence grid and the workspace rail.
function Pager({ page, total, onPrev, onNext }) {
  if (total <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 pt-2 shrink-0">
      <button onClick={onPrev} disabled={page === 0} title="Previous page"
        className="w-7 h-7 rounded-full border border-white/10 bg-[#131313] grid place-items-center text-white/70 hover:text-[#00FF99] hover:border-[#00FF99]/40 disabled:opacity-30 disabled:hover:text-white/70 disabled:hover:border-white/10 transition-colors">
        <ChevronLeft size={15} />
      </button>
      <span className="text-[11px] font-semibold text-white/60 tabular-nums min-w-[52px] text-center">Page {page + 1}/{total}</span>
      <button onClick={onNext} disabled={page >= total - 1} title="Next page"
        className="w-7 h-7 rounded-full border border-white/10 bg-[#131313] grid place-items-center text-white/70 hover:text-[#00FF99] hover:border-[#00FF99]/40 disabled:opacity-30 disabled:hover:text-white/70 disabled:hover:border-white/10 transition-colors">
        <ChevronRight size={15} />
      </button>
    </div>
  );
}

// Proportional equal-tile grid (Presence video sub-mode). cols = ceil(sqrt(n)),
// rows = ceil(n/cols) → 1:full · 2:1×2 · 3–4:2×2 · 5–6:2×3 · 7–9:3×3 · 10–12:3×4.
// All tiles are identical 16:9 boxes (object-cover video), sized to the measured
// container so they fill the space without distortion. Pages at 12 tiles.
function ParticipantGrid({ participants, cameraTracks, waiting }) {
  const [page, setPage] = React.useState(0);
  const [gridRef, { width, height }] = useElementSize();

  const camFor = (p) => cameraTracks.find((t) => t.participant?.identity === p?.identity);

  // Stable base order: local first, then remotes by identity (no jumping).
  const stable = [...participants].sort((a, b) => {
    if (a.isLocal !== b.isLocal) return a.isLocal ? -1 : 1;
    return String(a.identity).localeCompare(String(b.identity));
  });

  // Prioritise local + active speaker onto page 1 ONLY when paged (>12), so a
  // single page never reshuffles when someone starts/stops speaking.
  let ordered = stable;
  if (stable.length > GRID_PAGE_SIZE) {
    const local   = stable.find((p) => p.isLocal);
    const speaker = stable.find((p) => !p.isLocal && p.isSpeaking);
    const front   = [local, speaker].filter(Boolean);
    ordered = [...front, ...stable.filter((p) => !front.includes(p))];
  }

  const tiles = ordered.map((p) => ({ key: p.identity, participant: p }));
  if (waiting) tiles.push({ key: "__waiting__", waiting: true });

  const totalPages = Math.max(1, Math.ceil(tiles.length / GRID_PAGE_SIZE));
  React.useEffect(() => { if (page > totalPages - 1) setPage(totalPages - 1); }, [totalPages, page]);
  const pageTiles = tiles.slice(page * GRID_PAGE_SIZE, page * GRID_PAGE_SIZE + GRID_PAGE_SIZE);

  const n    = Math.max(1, pageTiles.length);
  const cols = Math.max(1, Math.ceil(Math.sqrt(n)));
  const rows = Math.max(1, Math.ceil(n / cols));
  const GAP  = 10;

  // Fit cols×rows of 16:9 tiles inside the measured box; bind by width or height.
  let tileW = 0, tileH = 0;
  if (width && height) {
    const availW = width  - GAP * (cols - 1);
    const availH = height - GAP * (rows - 1);
    const byW = availW / cols;
    if ((byW * 9) / 16 * rows <= availH) { tileW = byW; tileH = (byW * 9) / 16; }
    else { tileH = availH / rows; tileW = (tileH * 16) / 9; }
  }
  const avatarSize = n <= 2 ? "lg" : n <= 6 ? "md" : "sm";

  return (
    <div className="flex flex-col w-full h-full min-h-0">
      <div ref={gridRef}
        className="flex-1 min-h-0 w-full grid place-content-center justify-items-center items-center"
        style={{
          gap: GAP,
          gridTemplateColumns: tileW ? `repeat(${cols}, ${tileW}px)` : `repeat(${cols}, minmax(0,1fr))`,
          gridTemplateRows:    tileH ? `repeat(${rows}, ${tileH}px)` : `repeat(${rows}, minmax(0,1fr))`,
        }}>
        {pageTiles.map((t) => t.waiting ? (
          <div key={t.key} className="w-full h-full rounded-2xl border border-dashed border-white/15 grid place-items-center text-white/40 text-[11px] text-center px-2">
            Waiting for others…
          </div>
        ) : (
          <VideoTile key={t.key} trackRef={camFor(t.participant)} participant={t.participant} size={avatarSize} className="w-full h-full" />
        ))}
      </div>
      <Pager page={page} total={totalPages}
        onPrev={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => Math.min(totalPages - 1, p + 1))} />
    </div>
  );
}

// ── In-call chat body (mounts inside the shared tabbed SidePanel; the tab
//    strip provides the header/close, so this renders messages + input only.
//    Behaviour is otherwise unchanged from the previous chat panel). ──
function ChatPanelBody({ myName }) {
  const { chatMessages, send } = useChat();
  const [input,     setInput]    = React.useState("");
  const [tag,       setTag]      = React.useState(null);
  const [showTags,  setShowTags] = React.useState(false);
  const [showEmoji, setShowEmoji]= React.useState(false);
  const bottomRef = React.useRef(null);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior:"smooth" });
  }, [chatMessages]);

  const sendMsg = () => {
    const text = input.trim();
    if (!text) return;
    send(tag ? `[${tag.toUpperCase()}] ${text}` : text);
    setInput(""); setTag(null); setShowTags(false);
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2">
        {chatMessages.length === 0 && (
          <div className="text-center text-white/20 text-xs mt-6">No messages yet. Say hello! 👋</div>
        )}
        {chatMessages.map((msg, i) => {
          const isMe = msg.from?.name === myName;
          const tagMatch = msg.message?.match(/^\[([A-Z]+)\] /);
          const msgTag   = tagMatch ? tagMatch[1].toLowerCase() : null;
          const msgText  = tagMatch ? msg.message.replace(tagMatch[0], "") : msg.message;
          const tagColor = msgTag && TAG_COLORS[msgTag];
          return (
            <div key={i} className="flex flex-col" style={{ alignItems: isMe ? "flex-end" : "flex-start" }}>
              {tagColor && (
                <span className="text-[9px] font-bold tracking-wide px-[7px] py-0.5 rounded-full mb-[3px] uppercase"
                  style={{ background:`${tagColor}18`, color:tagColor, border:`1px solid ${tagColor}40` }}>{msgTag}</span>
              )}
              <span className="text-white/25 text-[9px] mb-0.5 pl-0.5">{msg.from?.name || "Unknown"}</span>
              <div className="max-w-[85%] px-3 py-2 text-xs text-white leading-relaxed break-words"
                style={{ borderRadius: isMe ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                         background: isMe ? `${NEON}18` : "rgba(255,255,255,0.07)",
                         border: isMe ? `1px solid ${NEON}30` : "1px solid rgba(255,255,255,0.06)" }}>{msgText}</div>
              <span className="text-white/[0.15] text-[9px] mt-0.5 pl-0.5">
                {new Date(msg.timestamp).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}
              </span>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Tag strip */}
      {showTags && (
        <div className="flex gap-1 px-3 py-1.5 flex-wrap border-t border-white/[0.06]">
          {TAGS.map(t => (
            <button key={t} onClick={() => { setTag(t === tag ? null : t); setShowTags(false); }}
              className="px-2.5 py-[3px] rounded-full text-[10px] font-semibold capitalize"
              style={{ background: tag===t ? TAG_COLORS[t] : `${TAG_COLORS[t]}18`, color: tag===t ? "#000" : TAG_COLORS[t] }}>{t}</button>
          ))}
        </div>
      )}

      {/* Emoji strip */}
      {showEmoji && (
        <div className="flex gap-1 px-3 py-2 flex-wrap border-t border-white/[0.06] bg-white/[0.02]">
          {EMOJIS.map(e => (
            <button key={e} onClick={() => { setInput(p => p+e); setShowEmoji(false); }} className="text-lg p-0.5">{e}</button>
          ))}
        </div>
      )}

      {/* Active tag pill */}
      {tag && (
        <div className="pt-1 px-3 flex items-center gap-1.5">
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize"
            style={{ background:`${TAG_COLORS[tag]}18`, color:TAG_COLORS[tag], border:`1px solid ${TAG_COLORS[tag]}40` }}>{tag}</span>
          <button onClick={() => setTag(null)} className="text-white/30 hover:text-white/60 text-[11px]"><X size={11} /></button>
        </div>
      )}

      {/* Input */}
      <div className="px-3 py-2.5 border-t border-white/[0.06] shrink-0">
        <div className="flex items-end gap-1.5 bg-white/5 border border-white/10 rounded-[14px] px-2.5 py-2">
          <button onClick={() => { setShowEmoji(p=>!p); setShowTags(false); }} className="text-white/40 hover:text-white/70 shrink-0"><Smile size={16} /></button>
          <button onClick={() => { setShowTags(p=>!p); setShowEmoji(false); }} className="shrink-0 rounded-md px-1"
            style={{ color: tag ? TAG_COLORS[tag] : "rgba(255,255,255,0.4)" }}><Tag size={15} /></button>
          <textarea value={input} onChange={e=>setInput(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendMsg();}}}
            placeholder="Message…" rows={1}
            className="flex-1 bg-transparent border-none outline-none text-white text-xs resize-none leading-relaxed max-h-20 overflow-y-auto"
            style={{ fontFamily:"inherit" }} />
          <button onClick={sendMsg} disabled={!input.trim()}
            className="w-[30px] h-[30px] rounded-full shrink-0 grid place-items-center transition-colors"
            style={{ background: input.trim() ? NEON : "rgba(255,255,255,0.08)", cursor: input.trim() ? "pointer" : "default" }}>
            <Send size={13} color={input.trim() ? "#000" : "rgba(255,255,255,0.4)"} />
          </button>
        </div>
        <p className="text-white/[0.15] text-[9px] mt-1 text-center">Shift+Enter for new line</p>
      </div>
    </div>
  );
}

// ── Shared-notes body — a controlled textarea. All state/sync/persistence
//    lives in CallBody so switching tabs (or closing the panel) never drops
//    the note or the data-channel subscription. ──
function NotesPanelBody({ content, onChange, loading }) {
  return (
    <div className="flex-1 min-h-0 flex flex-col" style={{ fontFamily: BODY }}>
      <div className="px-3.5 pt-3 pb-2 text-[10px] text-white/40 flex items-center gap-1.5 shrink-0" style={HEAD}>
        <StickyNote size={12} /> Shared notes · live · saved to this call
      </div>
      <textarea
        value={content}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        placeholder={loading ? "Loading notes…" : "Type shared notes — everyone on the call sees these live, and they're saved to the call."}
        className="flex-1 min-h-0 w-full resize-none bg-transparent border-none outline-none text-white text-[13px] leading-relaxed px-3.5 pb-3 placeholder:text-white/25 disabled:opacity-60"
        style={{ fontFamily: BODY }}
      />
    </div>
  );
}

// ── Whiteboard ────────────────────────────────────────────────
// Strokes are stored NORMALISED (points in 0..1 of canvas w/h) so every peer /
// canvas size / the offscreen snapshot render identically. The stroke list is
// the source of truth — the pixel buffer is always re-derived from it.
const BOARD_COLORS = ["#00FF99", "#ffffff", "#ff5f56", "#00BFFF", "#FFC24B"];
const BOARD_WIDTHS = [2, 4, 8];

function renderStrokesToCanvas(canvas, strokes, background = null) {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  const W = canvas.width, H = canvas.height;
  ctx.clearRect(0, 0, W, H);
  if (background) { ctx.fillStyle = background; ctx.fillRect(0, 0, W, H); }
  ctx.lineCap = "round"; ctx.lineJoin = "round";
  for (const s of (strokes || [])) {
    const pts = s?.points;
    if (!pts || pts.length === 0) continue;
    ctx.globalCompositeOperation = s.erase ? "destination-out" : "source-over";
    ctx.strokeStyle = s.color || "#00FF99";
    ctx.lineWidth = s.width || 3;
    ctx.beginPath();
    ctx.moveTo(pts[0].x * W, pts[0].y * H);
    if (pts.length === 1) ctx.lineTo(pts[0].x * W + 0.1, pts[0].y * H);   // dot
    else for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * W, pts[i].y * H);
    ctx.stroke();
  }
  ctx.globalCompositeOperation = "source-over";
}

// Interactive canvas + tool strip. Drawing tool state is local (resets on
// remount — fine); the stroke list lives in CallBody and is passed in.
function WhiteboardCanvas({ strokes, onStroke, onClear, className = "" }) {
  const canvasRef = React.useRef(null);
  const wrapRef   = React.useRef(null);
  const drawingRef = React.useRef(null);   // in-progress stroke
  const [color,   setColor]   = React.useState("#00FF99");
  const [width,   setWidth]   = React.useState(4);
  const [erasing, setErasing] = React.useState(false);

  // Size the backing store to the display box (DPR-aware), then redraw.
  const resize = React.useCallback(() => {
    const cv = canvasRef.current, wrap = wrapRef.current;
    if (!cv || !wrap) return;
    const r = wrap.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    cv.width  = Math.max(1, Math.round(r.width  * dpr));
    cv.height = Math.max(1, Math.round(r.height * dpr));
    renderStrokesToCanvas(cv, strokes);
  }, [strokes]);

  React.useEffect(() => {
    resize();
    const ro = new ResizeObserver(resize);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [resize]);

  // Redraw the full list whenever it changes (remote strokes / clear / sync).
  React.useEffect(() => { renderStrokesToCanvas(canvasRef.current, strokes); }, [strokes]);

  const norm = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };

  const start = (e) => {
    e.preventDefault();
    try { canvasRef.current?.setPointerCapture?.(e.pointerId); } catch (_) {}
    drawingRef.current = { points: [norm(e)], color, width: erasing ? Math.max(width * 4, 18) : width, erase: erasing };
  };
  const move = (e) => {
    const cur = drawingRef.current;
    if (!cur) return;
    e.preventDefault();
    const p = norm(e), prev = cur.points[cur.points.length - 1];
    cur.points.push(p);
    // Incremental segment draw (no full redraw per point).
    const cv = canvasRef.current, ctx = cv.getContext("2d"), W = cv.width, H = cv.height;
    ctx.globalCompositeOperation = cur.erase ? "destination-out" : "source-over";
    ctx.strokeStyle = cur.color; ctx.lineWidth = cur.width; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.beginPath(); ctx.moveTo(prev.x * W, prev.y * H); ctx.lineTo(p.x * W, p.y * H); ctx.stroke();
    ctx.globalCompositeOperation = "source-over";
  };
  const end = () => {
    const s = drawingRef.current;
    drawingRef.current = null;
    if (s && s.points.length > 0) onStroke(s);   // commit + broadcast (batched on stroke end)
  };

  const swatch = (c) => (
    <button key={c} onClick={() => { setColor(c); setErasing(false); }}
      className={`w-5 h-5 rounded-full border-2 ${color === c && !erasing ? "border-white" : "border-white/20"}`}
      style={{ background: c }} title="Pen colour" />
  );

  return (
    <div className={`flex flex-col min-h-0 ${className}`} style={{ fontFamily: BODY }}>
      {/* Tool strip */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/[0.06] shrink-0 flex-wrap">
        {BOARD_COLORS.map(swatch)}
        <div className="w-px h-4 bg-white/10 mx-0.5" />
        {BOARD_WIDTHS.map((w) => (
          <button key={w} onClick={() => { setWidth(w); setErasing(false); }}
            className={`w-6 h-6 grid place-items-center rounded-md ${width === w && !erasing ? "bg-[#00FF99]/15 border border-[#00FF99]/40" : "border border-transparent hover:bg-white/5"}`}
            title={`Stroke ${w}`}>
            <span className="rounded-full bg-white" style={{ width: w + 2, height: w + 2 }} />
          </button>
        ))}
        <div className="w-px h-4 bg-white/10 mx-0.5" />
        <button onClick={() => setErasing((v) => !v)} title="Eraser"
          className={`w-7 h-7 grid place-items-center rounded-md ${erasing ? "bg-[#00FF99]/15 border border-[#00FF99]/40 text-[#00FF99]" : "text-white/60 border border-transparent hover:bg-white/5"}`}>
          <Eraser size={15} />
        </button>
        <button onClick={onClear} title="Clear board"
          className="w-7 h-7 grid place-items-center rounded-md text-white/60 border border-transparent hover:bg-[#ff5f56]/15 hover:text-[#ff5f56] ml-auto">
          <Trash2 size={15} />
        </button>
      </div>
      {/* Canvas — transparent, over a dotted dark surface; eraser reveals it. */}
      <div ref={wrapRef} className="flex-1 min-h-0 relative bg-[#131313]"
        style={{ backgroundImage: "radial-gradient(rgba(255,255,255,.07) 1px, transparent 1px)", backgroundSize: "22px 22px" }}>
        <canvas ref={canvasRef}
          className="absolute inset-0 w-full h-full touch-none cursor-crosshair"
          onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerCancel={end} onPointerLeave={end} />
      </div>
    </div>
  );
}

// ── Tabbed side panel (Chat / Notes / Whiteboard) — the generalised slide-over,
//    shared by both variants. Whiteboard is only shown here in Presence; in
//    Workspace it lives in the stage (so it's routed there before this renders).
//    Bodies keep their own concerns; this only owns the tab strip + close. ──
function SidePanel({ tab, onTab, onClose, myName, notesContent, onNotesChange, notesLoading, whiteboard, wide }) {
  const tabBtn = (on) =>
    `flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-full transition-colors ${
      on ? "bg-[#00FF99]/15 text-[#00FF99] border border-[#00FF99]/30"
         : "text-white/55 border border-transparent hover:text-white/80"}`;
  return (
    <div style={{ fontFamily: BODY }} className={`${wide ? "w-[560px]" : "w-[320px]"} h-full flex flex-col bg-[#0a0a0a] border-l border-[#00FF99]/10`}>
      {/* Tab strip */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/[0.06] shrink-0">
        <div className="flex gap-1">
          <button onClick={() => onTab("chat")}       className={tabBtn(tab === "chat")}><MessageSquare size={13} /> Chat</button>
          <button onClick={() => onTab("notes")}      className={tabBtn(tab === "notes")}><StickyNote size={13} /> Notes</button>
          <button onClick={() => onTab("whiteboard")} className={tabBtn(tab === "whiteboard")}><PenLine size={13} /> Board</button>
        </div>
        <button onClick={onClose} className="text-white/30 hover:text-white/70 p-1 rounded-md transition-colors"><X size={16} /></button>
      </div>
      {/* Body — mount the active tab only. Notes/whiteboard state lives in
          CallBody so it survives tab switches; chat unmounts when hidden. */}
      {tab === "chat"       && <ChatPanelBody myName={myName} />}
      {tab === "notes"      && <NotesPanelBody content={notesContent} onChange={onNotesChange} loading={notesLoading} />}
      {tab === "whiteboard" && <div className="flex-1 min-h-0 flex flex-col">{whiteboard}</div>}
    </div>
  );
}

// ── A single control button (circular, lucide icon) ───────────
function CtrlBtn({ children, onClick, off = false, active = false, disabled = false, title, wide = false }) {
  const base = "relative h-10 rounded-full border grid place-items-center transition-colors shrink-0 disabled:opacity-60";
  const shape = wide ? "px-4 gap-2 flex text-[12px] font-semibold" : "w-10";
  let tone = "bg-[#131313] border-white/10 text-white hover:border-[#00FF99]/40 hover:text-[#00FF99]";
  if (off)    tone = "bg-[#ff5f56]/15 border-[#ff5f56]/40 text-[#ff5f56] hover:border-[#ff5f56]/60";
  if (active) tone = "bg-[#00FF99]/15 border-[#00FF99]/50 text-[#00FF99]";
  return (
    <button onClick={onClick} disabled={disabled} title={title} className={`${base} ${shape} ${tone}`}>
      {children}
    </button>
  );
}

// ── Bottom control cluster — mic / camera / screen-share / record / chat ──
// Mute + camera + screen-share act on the SAME LiveKit local participant that
// the old ControlBar drove, so behaviour is unchanged.
function CallControls({ audioOnly, recording, uploading, recDuration, onToggleRecord, panelTab, onSelectPanel, className = "" }) {
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled, isScreenShareEnabled } = useLocalParticipant();

  const toggleMic   = () => localParticipant?.setMicrophoneEnabled(!isMicrophoneEnabled);
  const toggleCam   = () => localParticipant?.setCameraEnabled(!isCameraEnabled);
  const toggleShare = () => localParticipant?.setScreenShareEnabled(!isScreenShareEnabled);

  return (
    <div className={`flex items-center gap-2 bg-[#0d0d0d]/90 border border-white/10 rounded-full p-2 backdrop-blur-md ${className}`}>
      <CtrlBtn onClick={toggleMic} off={!isMicrophoneEnabled} title={isMicrophoneEnabled ? "Mute" : "Unmute"}>
        {isMicrophoneEnabled ? <Mic size={17} /> : <MicOff size={17} />}
      </CtrlBtn>
      {!audioOnly && (
        <CtrlBtn onClick={toggleCam} off={!isCameraEnabled} title={isCameraEnabled ? "Stop camera" : "Start camera"}>
          {isCameraEnabled ? <Video size={17} /> : <VideoOff size={17} />}
        </CtrlBtn>
      )}
      <CtrlBtn onClick={toggleShare} active={isScreenShareEnabled} title={isScreenShareEnabled ? "Stop sharing" : "Share screen"}>
        {isScreenShareEnabled ? <ScreenShareOff size={17} /> : <ScreenShare size={17} />}
      </CtrlBtn>
      <CtrlBtn onClick={onToggleRecord} off={recording} disabled={uploading} title={recording ? "Stop recording" : "Record"}>
        {recording ? <Square size={15} fill="currentColor" /> : <Circle size={17} />}
      </CtrlBtn>
      {recording && (
        <span className="text-[11px] font-semibold text-[#ff5f56] tabular-nums pr-1">{fmt(recDuration)}</span>
      )}
      {uploading && (
        <span className="text-[11px] font-semibold text-white/50 pr-1 flex items-center gap-1">
          <Loader2 size={12} className="animate-spin" /> Saving…
        </span>
      )}
      <CtrlBtn onClick={() => onSelectPanel("chat")}       active={panelTab === "chat"}       title="Chat"><MessageSquare size={17} /></CtrlBtn>
      <CtrlBtn onClick={() => onSelectPanel("notes")}      active={panelTab === "notes"}      title="Shared notes"><StickyNote size={17} /></CtrlBtn>
      <CtrlBtn onClick={() => onSelectPanel("whiteboard")} active={panelTab === "whiteboard"} title="Whiteboard"><PenLine size={17} /></CtrlBtn>
    </div>
  );
}

// ── A person tile: real video when the camera is live, branded avatar otherwise ──
function VideoTile({ trackRef, participant, size = "md", className = "" }) {
  const p = participant || trackRef?.participant;
  const name = p?.name || p?.identity || "Guest";
  const speaking = !!p?.isSpeaking;
  const muted = p && p.isMicrophoneEnabled === false;
  const avatarSize = size === "lg" ? "w-[86px] h-[86px] text-[28px]" : size === "sm" ? "w-11 h-11 text-[15px]" : "w-[52px] h-[52px] text-lg";

  return (
    <div
      className={`relative rounded-2xl overflow-hidden grid place-items-center border ${speaking ? "border-[#00FF99]/30" : "border-white/10"} ${className}`}
      style={{ background:"radial-gradient(220px 170px at 60% 30%,#182420,#0a0d0c 75%)",
               boxShadow: speaking ? "0 0 16px rgba(0,255,153,.2)" : "none" }}>
      {hasLiveVideo(trackRef)
        ? <ParticipantTile trackRef={trackRef} className="absolute inset-0 !rounded-2xl" style={{ borderRadius:16, overflow:"hidden" }} />
        : <div className={`rounded-full bg-[#131313] border border-[#00FF99]/30 grid place-items-center text-[#00FF99] font-semibold ${avatarSize}`} style={HEAD}>{initials(name)}</div>}
      <div className="absolute left-2 bottom-2 flex gap-1.5 items-center bg-[#050505]/75 border border-white/10 rounded-full px-2.5 py-1 text-[10px] font-semibold backdrop-blur-sm">
        {muted ? <MicOff size={11} color="#ff5f56" /> : <Mic size={11} color={NEON} />}
        <span className="text-white">{p?.isLocal ? "You" : name}{speaking ? " · speaking" : ""}</span>
      </div>
    </div>
  );
}

// ── A pulsing presence orb (audio view) ───────────────────────
function Orb({ participant, quiet = false }) {
  const name = participant?.name || participant?.identity || "Guest";
  const speaking = !quiet && !!participant?.isSpeaking;
  const muted = participant && participant.isMicrophoneEnabled === false;
  return (
    <div className="text-center">
      <div className="relative w-[150px] h-[150px] rounded-full grid place-items-center mx-auto"
        style={{ ...HEAD, fontSize:42, fontWeight:600, color: quiet ? "rgba(255,255,255,.55)" : NEON,
                 background:"radial-gradient(circle at 35% 30%,#1c2b24,#0b120e 70%)",
                 border:`1px solid ${quiet ? "rgba(255,255,255,.10)" : "rgba(0,255,153,.30)"}` }}>
        {initials(name)}
        {speaking && (
          <>
            <span className="absolute rounded-full" style={{ inset:-14, border:"1.5px solid rgba(0,255,153,.35)", animation:"kwOrb 2.2s ease-out infinite" }} />
            <span className="absolute rounded-full" style={{ inset:-28, border:"1.5px solid rgba(0,255,153,.18)", animation:"kwOrb 2.2s ease-out infinite .55s" }} />
            <span className="absolute rounded-full" style={{ inset:-44, border:"1.5px solid rgba(0,255,153,.08)", animation:"kwOrb 2.2s ease-out infinite 1.1s" }} />
          </>
        )}
      </div>
      <div className="mt-4 text-[13px] font-semibold text-white">{participant?.isLocal ? "You" : name}</div>
      <div className={`text-[10px] mt-0.5 flex gap-1.5 items-center justify-center ${muted ? "text-[#ff5f56]" : "text-white/55"}`}>
        {muted ? <MicOff size={11} /> : <Mic size={11} color={speaking ? NEON : "currentColor"} />}
        {muted ? "muted" : speaking ? "speaking…" : "connected"}
      </div>
    </div>
  );
}

// ── Presence variant — people-first ambient view ──────────────
// Audio sub-mode = pulsing orbs; Video sub-mode = proportional equal-tile grid
// (ParticipantGrid). Screen-share is handled by the Workspace variant / stage,
// so it does not affect these orb/grid modes.
function PresenceLayout({ participants, cameraTracks, mode, onMode, waiting, targetName, controls }) {
  return (
    <div className="relative h-full w-full"
      style={{ background:"radial-gradient(700px 500px at 50% 42%,#0a1410,#050505 70%)" }}>

      {/* Audio / Video sub-toggle (display only — does not change the media) */}
      <div className="absolute top-6 left-1/2 -translate-x-1/2 z-10 flex gap-1 bg-[#0d0d0d] border border-white/10 rounded-full p-[3px]">
        {["audio","video"].map(m => (
          <button key={m} onClick={() => onMode(m)}
            className={`text-[10px] font-semibold px-3.5 py-[5px] rounded-full capitalize ${mode===m ? "bg-[#00FF99]/15 text-[#00FF99] border border-[#00FF99]/30" : "text-white/55 border border-transparent"}`}>
            {m}
          </button>
        ))}
      </div>

      {mode === "audio" ? (
        <div className="h-full w-full flex flex-col items-center justify-center px-4">
          <div className="flex gap-[90px] items-center flex-wrap justify-center">
            {participants.map(p => <Orb key={p.identity} participant={p} />)}
            {waiting && (
              <div className="text-center opacity-60">
                <div className="w-[150px] h-[150px] rounded-full grid place-items-center mx-auto border border-dashed border-white/15 text-white/40" style={HEAD}>
                  <Loader2 size={30} className="animate-spin" />
                </div>
                <div className="mt-4 text-[13px] font-semibold text-white/60">Waiting for others…</div>
              </div>
            )}
          </div>
          {/* Caption slot — connection status (transcript surface is not wired to
              a data source, so it is intentionally omitted rather than faked). */}
          <div className="mt-14 max-w-[560px] text-center text-white/60 text-[13px] min-h-[24px] px-4">
            {waiting ? `Ringing${targetName ? ` ${targetName}` : ""}… you'll connect the moment they join.` : ""}
          </div>
        </div>
      ) : (
        <div className="h-full w-full pt-16 pb-24 px-4 flex">
          <ParticipantGrid participants={participants} cameraTracks={cameraTracks} waiting={waiting} />
        </div>
      )}

      {controls && <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">{controls}</div>}
    </div>
  );
}

// ── Workspace variant — people rail + shared-work stage ───────
function WorkspaceLayout({ participants, cameraTracks, screenTracks, waiting, controls, secs, timerStarted, recording, audioOnly,
                           boardActive, onShowBoard, onHideBoard, whiteboard }) {
  const screen = screenTracks[0];
  const speaker = participants.find(p => !p.isLocal && p.isSpeaking)
    || participants.find(p => !p.isLocal)
    || participants[0];
  const camFor = (p) => cameraTracks.find(t => t.participant?.identity === p?.identity);
  const sharerName = screen?.participant?.name || screen?.participant?.identity || "Someone";

  // Rail equal-sizes every tile to 16:9 and pages at 12 when the call is large.
  const [railPage, setRailPage] = React.useState(0);
  const railTotal = Math.max(1, Math.ceil(participants.length / GRID_PAGE_SIZE));
  React.useEffect(() => { if (railPage > railTotal - 1) setRailPage(railTotal - 1); }, [railTotal, railPage]);
  const railTiles = participants.slice(railPage * GRID_PAGE_SIZE, railPage * GRID_PAGE_SIZE + GRID_PAGE_SIZE);

  return (
    <div className="flex h-full w-full" style={{ background:"#050505" }}>
      {/* People rail */}
      <div className="w-[210px] shrink-0 border-r border-white/10 p-3 flex flex-col gap-2.5 bg-[#0d0d0d]">
        <div className="text-[10px] font-semibold text-white/40 flex items-center gap-1.5 px-1" style={HEAD}>
          <Users size={12} /> On this call{participants.length > 1 ? ` · ${participants.length}` : ""}
        </div>
        <div className="flex-1 min-h-0 flex flex-col gap-2.5 overflow-y-auto">
          {railTiles.map(p => (
            <VideoTile key={p.identity} trackRef={camFor(p)} participant={p} size="sm" className="w-full aspect-video shrink-0" />
          ))}
          {waiting && railPage === railTotal - 1 && (
            <div className="w-full aspect-video shrink-0 rounded-2xl border border-dashed border-white/15 grid place-items-center text-white/40 text-[11px] text-center px-2">
              Waiting for others…
            </div>
          )}
        </div>
        <Pager page={railPage} total={railTotal}
          onPrev={() => setRailPage((p) => Math.max(0, p - 1))}
          onNext={() => setRailPage((p) => Math.min(railTotal - 1, p + 1))} />
        <div className="text-center text-[10px] text-white/55 mt-1">
          {recording && <span className="text-[#ff5f56] font-semibold">● REC</span>}
          {recording && " · "}
          {timerStarted ? fmt(secs) : "connecting…"} · {audioOnly ? "audio call" : "video call"}
        </div>
        {controls && <div className="mt-auto flex justify-center">{controls}</div>}
      </div>

      {/* Stage — screen / speaker, OR the shared whiteboard (chip-selected) */}
      <div className="flex-1 min-w-0 flex flex-col p-4 gap-2.5">
        <div className="flex items-center gap-2.5">
          <span className="text-[15px] font-semibold text-white" style={HEAD}>
            {boardActive ? "Whiteboard" : screen ? "Shared screen" : "Stage"}
          </span>
          {!boardActive && screen && (
            <span className="text-[9px] text-[#00FF99] border border-[#00FF99]/30 bg-[#00FF99]/10 px-2 py-0.5 rounded-full">
              {sharerName} · SHARING LIVE
            </span>
          )}
          {/* Stage tabs — call view ↔ whiteboard (design's stage chips) */}
          <div className="ml-auto flex gap-1.5">
            <button onClick={onHideBoard}
              className={`text-[10px] font-semibold px-3 py-1 rounded-full border ${!boardActive ? "text-[#00FF99] bg-[#00FF99]/10 border-[#00FF99]/30" : "text-white/55 bg-[#0d0d0d] border-white/10 hover:text-white/80"}`}>
              {screen ? "Screen" : "Call"}
            </button>
            <button onClick={onShowBoard}
              className={`flex items-center gap-1 text-[10px] font-semibold px-3 py-1 rounded-full border ${boardActive ? "text-[#00FF99] bg-[#00FF99]/10 border-[#00FF99]/30" : "text-white/55 bg-[#0d0d0d] border-white/10 hover:text-white/80"}`}>
              <PenLine size={11} /> Whiteboard
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 rounded-[18px] border border-[#00FF99]/30 bg-[#0d0d0d] overflow-hidden grid place-items-center"
          style={{ boxShadow:"inset 0 0 40px rgba(0,255,153,.03)" }}>
          {boardActive ? (
            <div className="w-full h-full flex flex-col">{whiteboard}</div>
          ) : waiting ? (
            <div className="text-center text-white/40">
              <Loader2 size={34} className="animate-spin mx-auto mb-3" />
              <p className="text-sm">Waiting for others to join…</p>
            </div>
          ) : screen ? (
            <ParticipantTile trackRef={screen} className="w-full h-full" style={{ borderRadius:18, overflow:"hidden" }} />
          ) : (
            <VideoTile trackRef={camFor(speaker)} participant={speaker} size="lg" className="w-[min(60%,640px)] aspect-video" />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Shared in-room body: owns ALL call logic, swaps only the layout ──
// Replaces the old KweryVideoLayout. Both variants render inside this one
// component, so they share a single LiveKit room, track set, recording session
// and timer — the toggle never remounts the room.
function CallBody({ myName, myId, roomId, participantIds, participantNames, onTimerStart, timerStarted, variant, audioOnly, targetName, secs, flushNotesRef, flushWhiteboardRef }) {
  const tracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }, { source: Track.Source.ScreenShare, withPlaceholder: false }],
    { onlySubscribed: false }
  );
  const participants = useParticipants();
  const { send: sendChatMsg } = useChat();

  const [panelTab,      setPanelTab]      = React.useState(null); // null | 'chat' | 'notes' | 'whiteboard'
  const [recording,     setRecording]     = React.useState(false);
  const [recDuration,   setRecDuration]   = React.useState(0);
  const [uploading,     setUploading]     = React.useState(false);
  const [recToast,      setRecToast]      = React.useState(null);
  const [presenceMode,  setPresenceMode]  = React.useState(audioOnly ? "audio" : "video");
  const mediaRecRef = React.useRef(null);
  const recTimerRef = React.useRef(null);

  const allIds   = participants.map(p => p.identity);

  // ── Shared notes ──────────────────────────────────────────────
  // Live sync over the LiveKit data channel (topic "kwery.notes") + debounced
  // persistence to /api/calling/notes. State lives HERE (not in the panel) so
  // switching tabs / closing the panel never drops the note or the subscription.
  const [notesContent, setNotesContent] = React.useState("");
  const [notesLoading, setNotesLoading] = React.useState(false);
  const notesContentRef  = React.useRef("");     // latest content (for flush/echo checks)
  const notesDirtyRef    = React.useRef(false);   // unsaved local edits pending
  const notesHydratedRef = React.useRef(false);   // GET-hydrated once per call
  const notesDebounceRef = React.useRef(null);

  // Remote note updates → replace local content (last-write-wins). Ignore our
  // own echo via senderId. There is one data channel; the topic only filters.
  // The handler is a STABLE useCallback (reads mutable state via refs) so
  // useDataChannel's memoised send + isSendingObservable survive re-renders and
  // stay subscribed — otherwise a send fired before the (post-commit) re-
  // subscription hits an undefined isSending subscriber and rejects.
  const onNoteMessage = React.useCallback((msg) => {
    try {
      const data = JSON.parse(new TextDecoder().decode(msg.payload));
      if (!data || String(data.senderId) === String(myId)) return; // self-echo
      const next = typeof data.content === "string" ? data.content : "";
      notesContentRef.current = next;
      notesDirtyRef.current   = false; // remote is now the source of truth
      setNotesContent(next);
    } catch (_) {}
  }, [myId]);
  const { send: sendNote } = useDataChannel("kwery.notes", onNoteMessage);

  const persistNotes = (val, opts = {}) => {
    return fetch(`${API_BASE}/api/calling/notes`, {
      method: "POST",
      keepalive: !!opts.keepalive,   // let the flush survive teardown
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
      body: JSON.stringify({ room_id: roomId, content: val }),
    });
  };

  const onNotesChange = (val) => {
    setNotesContent(val);
    notesContentRef.current = val;
    notesDirtyRef.current   = true;
    if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current);
    notesDebounceRef.current = setTimeout(() => {
      // Broadcast to peers… (async send: guard the rejection so a transport
      // failure degrades to a dropped message, never an unhandled rejection).
      try {
        const payload = new TextEncoder().encode(JSON.stringify({ content: val, senderId: myId, ts: Date.now() }));
        sendNote?.(payload, { topic: "kwery.notes", reliable: true })?.catch(() => {});
      } catch (_) {}
      // …and persist (clear dirty only if the content hasn't changed since).
      persistNotes(val)
        .then(() => { if (notesContentRef.current === val) notesDirtyRef.current = false; })
        .catch(() => {});
    }, 500);
  };

  // Flush-on-end: fire a best-effort save if there are unsaved edits. Wired into
  // VideoCallModal's close path via flushNotesRef; must not block teardown.
  // Plain function (re-created each render) so the ref always holds the latest
  // closure — assigned below on every render.
  const flushNotes = () => {
    if (!notesDirtyRef.current) return;
    try { persistNotes(notesContentRef.current, { keepalive: true }).catch(() => {}); } catch (_) {}
    notesDirtyRef.current = false;
  };
  React.useEffect(() => { if (flushNotesRef) flushNotesRef.current = flushNotes; });

  // Hydrate the shared note the first time the Notes tab is opened this call.
  React.useEffect(() => {
    if (panelTab !== "notes" || notesHydratedRef.current) return;
    notesHydratedRef.current = true;
    setNotesLoading(true);
    fetch(`${API_BASE}/api/calling/notes?room_id=${encodeURIComponent(roomId || "")}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        // Don't clobber edits the user may have started before hydration returned.
        if (d && typeof d.content === "string" && !notesDirtyRef.current) {
          notesContentRef.current = d.content;
          setNotesContent(d.content);
        }
      })
      .catch(() => {})
      .finally(() => setNotesLoading(false));
  }, [panelTab, roomId]);

  // ── Shared whiteboard ─────────────────────────────────────────
  // Live stroke sync over the LiveKit data channel (topic "kwery.whiteboard").
  // Stroke list is the source of truth and lives HERE so it survives tab
  // switches AND the variant toggle (canvas re-derives pixels from it on mount).
  const [strokes, setStrokes]   = React.useState([]);
  const strokesRef              = React.useRef([]);
  const boardHydratedRef        = React.useRef(false);
  const sendBoardRef            = React.useRef(null);   // holds useDataChannel's send
  React.useEffect(() => { strokesRef.current = strokes; }, [strokes]);

  // Defined before useDataChannel (so the receive handler can call it) but sends
  // via a ref that the hook fills in just below — one data channel, topic filter.
  // STABLE (useCallback) so it can sit in the receive handler's deps without
  // destabilising it. The send is async: guard the rejection (.catch) so a
  // transport failure degrades to a dropped message, never an unhandled
  // rejection — covers stroke / clear / the sync_state reply below.
  const boardSend = React.useCallback((obj) => {
    try {
      const payload = new TextEncoder().encode(JSON.stringify({ ...obj, senderId: myId }));
      sendBoardRef.current?.(payload, { topic: "kwery.whiteboard", reliable: true })?.catch(() => {});
    } catch (_) {}
  }, [myId]);

  // STABLE receive handler (reads strokes via strokesRef) so useDataChannel's
  // memoised send + isSendingObservable survive re-renders and stay subscribed.
  const onBoardMessage = React.useCallback((msg) => {
    try {
      const data = JSON.parse(new TextDecoder().decode(msg.payload));
      if (!data || String(data.senderId) === String(myId)) return; // self-echo
      if (data.type === "stroke" && Array.isArray(data.points)) {
        setStrokes((prev) => [...prev, { points: data.points, color: data.color, width: data.width, erase: !!data.erase }]);
      } else if (data.type === "clear") {
        setStrokes([]);
      } else if (data.type === "sync_request") {
        // A peer just opened the board — replay our strokes if we have any.
        if (strokesRef.current.length > 0) boardSend({ type: "sync_state", strokes: strokesRef.current });
      } else if (data.type === "sync_state" && Array.isArray(data.strokes)) {
        // Adopt only while still blank — never clobber local work.
        if (strokesRef.current.length === 0) setStrokes(data.strokes);
      }
    } catch (_) {}
  }, [myId, boardSend]);
  const { send: sendBoard } = useDataChannel("kwery.whiteboard", onBoardMessage);
  sendBoardRef.current = sendBoard;

  // Local edits: commit + broadcast (batched — one message per finished stroke).
  const addStroke = (stroke) => {
    setStrokes((prev) => [...prev, stroke]);
    boardSend({ type: "stroke", points: stroke.points, color: stroke.color, width: stroke.width, erase: !!stroke.erase });
  };
  const clearBoard = () => {
    setStrokes([]);
    boardSend({ type: "clear" });
  };

  // Hydration handshake: on the first whiteboard open this call, ask peers for
  // the current board. Any peer with strokes replies sync_state; if none reply
  // within ~1.5s we simply stay blank (nothing further to do).
  React.useEffect(() => {
    if (panelTab !== "whiteboard" || boardHydratedRef.current) return;
    boardHydratedRef.current = true;
    // Inline (not boardSend) so the dep list stays stable — send via the ref.
    // Guard the async rejection like the other send sites.
    try {
      const payload = new TextEncoder().encode(JSON.stringify({ type: "sync_request", senderId: myId }));
      sendBoardRef.current?.(payload, { topic: "kwery.whiteboard", reliable: true })?.catch(() => {});
    } catch (_) {}
  }, [panelTab, myId]);

  // Snapshot on end: render the stroke list to an OFFSCREEN canvas (mount-
  // independent) → PNG → best-effort multipart upload. Wired via flushWhiteboardRef.
  const snapshotWhiteboard = () => {
    const list = strokesRef.current;
    if (!list || list.length === 0) return;
    try {
      const off = document.createElement("canvas");
      off.width = 1280; off.height = 720;
      renderStrokesToCanvas(off, list, "#0d0d0d");   // dark bg so the PNG isn't transparent
      off.toBlob((blob) => {
        if (!blob) return;
        const fd = new FormData();
        fd.append("file", blob, "whiteboard.png");
        fd.append("room_id", roomId || "");
        fetch(`${API_BASE}/api/calling/whiteboard/upload`, {
          method: "POST", keepalive: true,
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: fd,
        }).catch(() => {});
      }, "image/png");
    } catch (_) {}
  };
  React.useEffect(() => { if (flushWhiteboardRef) flushWhiteboardRef.current = snapshotWhiteboard; });

  // Start timer when the second participant joins → onTimerStart (VideoCallModal
  // starts the clock AND calls onAnswered, clearing the caller's ringing card).
  // Unchanged from the previous implementation.
  React.useEffect(() => {
    if (!timerStarted && tracks.length >= 2) onTimerStart?.();
  }, [tracks.length, timerStarted, onTimerStart]);

  const startRecording = async () => {
    // 1. Consent BEFORE capture: notify all participants and WAIT for it to
    //    succeed. The consent notice appears on every screen (pushed over
    //    presence as call_recording_notice → CallContext) before any
    //    MediaRecorder starts.
    let notified = false;
    try {
      const r = await fetch(`${API_BASE}/api/calling/notify-recording`, {
        method: "POST",
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ room_id:roomId, recorder_name:myName, participant_ids:[...new Set([...allIds, ...participantIds])], action:"started" }),
      });
      notified = r.ok;
    } catch(_) { notified = false; }
    if (!notified) {
      // Do not record if the consent notice could not be dispatched.
      console.warn("Recording aborted — consent notice could not be sent to participants");
      return;
    }

    // 2. Consent dispatched — NOW begin capture. Recording stays user-initiated.
    setRecording(true);
    recTimerRef.current = setInterval(() => setRecDuration(d => d+1), 1000);

    try {
      // Strategy: capture local camera/mic + mix in all remote audio from the DOM
      // No screen share prompt — fully automatic
      let stream;
      try {
        // 1. Get local camera + mic
        const localStream = await navigator.mediaDevices.getUserMedia({ video:true, audio:true });

        // 2. Mix all audio (local + remote) using AudioContext
        const audioCtx = new AudioContext();
        const dest     = audioCtx.createMediaStreamDestination();

        // Add local mic
        audioCtx.createMediaStreamSource(localStream).connect(dest);

        // Add all remote audio elements rendered by LiveKit
        document.querySelectorAll("audio").forEach(el => {
          try {
            if (el.srcObject) {
              audioCtx.createMediaStreamSource(el.srcObject).connect(dest);
            }
          } catch(_) {}
        });

        // 3. Combine local video + mixed audio into one stream
        stream = new MediaStream([
          ...localStream.getVideoTracks(),
          ...dest.stream.getAudioTracks(),
        ]);

        // Cleanup audioCtx when done
        mediaRecRef._audioCtx = audioCtx;
        mediaRecRef._localStream = localStream;
      } catch(_) {
        // Fallback — mic only
        stream = await navigator.mediaDevices.getUserMedia({ audio:true, video:false });
      }

      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus" : "video/webm";
      const mr = new MediaRecorder(stream, { mimeType });
      const chunks = [];
      mr.ondataavailable = e => { if(e.data.size > 0) chunks.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        try { mediaRecRef._localStream?.getTracks().forEach(t=>t.stop()); } catch(_){}
        try { mediaRecRef._audioCtx?.close(); } catch(_){}
        clearInterval(recTimerRef.current);
        setUploading(true);
        const blob = new Blob(chunks, { type:"video/webm" });
        const fd   = new FormData();
        fd.append("file",              blob, "recording.webm");
        fd.append("room_id",           roomId);
        fd.append("participant_ids",   JSON.stringify([...new Set([...allIds, ...participantIds])]));
        fd.append("participant_names", JSON.stringify(participantNames));
        fd.append("duration_secs",     String(recDuration));
        try {
          const res  = await fetch(`${API_BASE}/api/calling/recordings/upload`, {
            method:"POST", headers:{ Authorization:`Bearer ${localStorage.getItem("token")}` }, body:fd,
          });
          const data = await res.json();
          // The recording id, NOT a download URL. The download endpoint is
          // authenticated now, so a URL pasted into chat or copied to the
          // clipboard is either a dead link or — if we attached a token to
          // make it work — a credential we handed out. Everything below
          // goes through the Recordings panel instead.
          setRecToast({ id: data.id, expires: data.expires_at });
          const expiryDate = new Date(data.expires_at).toLocaleDateString();
          const callDate   = new Date().toLocaleString([], { dateStyle:"medium", timeStyle:"short" });
          try {
            sendChatMsg(`📼 Recording ready · ${callDate} · Expires ${expiryDate}
Open it from Recordings in the chat sidebar.`);
          } catch(_) {}
          // Backend sends DM to all participants automatically
        } catch(e) { console.error("Upload failed:", e); }
        setUploading(false); setRecording(false); setRecDuration(0);
      };
      mr.start(1000);
      mediaRecRef.current = mr;
    } catch(e) {
      console.error("Capture failed:", e);
      clearInterval(recTimerRef.current);
      setRecording(false); setRecDuration(0);
    }
  };

  const stopRecording = () => {
    mediaRecRef.current?.stop();
    clearInterval(recTimerRef.current);
  };

  const cameraTracks = tracks.filter(t => t.source === Track.Source.Camera);
  const screenTracks = tracks.filter(t => t.source === Track.Source.ScreenShare);
  const waiting = participants.filter(p => !p.isLocal).length === 0;

  const selectPanel = (t) => setPanelTab((cur) => (cur === t ? null : t));

  const controls = (
    <CallControls
      audioOnly={audioOnly}
      recording={recording}
      uploading={uploading}
      recDuration={recDuration}
      onToggleRecord={recording ? stopRecording : startRecording}
      panelTab={panelTab}
      onSelectPanel={selectPanel}
    />
  );

  // The single whiteboard instance — rendered in the Workspace stage OR the
  // Presence slide-over (never both at once), always fed by CallBody's strokes.
  const whiteboard = <WhiteboardCanvas strokes={strokes} onStroke={addStroke} onClear={clearBoard} className="h-full" />;

  // Surface rules: whiteboard goes to the WORKSPACE stage; in PRESENCE it uses a
  // widened (560px) slide-over. Chat/Notes always use the 320px slide-over.
  const boardInStage = variant === "workspace" && panelTab === "whiteboard";
  const slideOpen    = !!panelTab && !boardInStage;
  const slideWidth   = panelTab === "whiteboard" ? 560 : 320;

  return (
    <div className="relative h-full w-full flex">
      <div className="flex-1 min-w-0 h-full">
        {variant === "workspace" ? (
          <WorkspaceLayout
            participants={participants}
            cameraTracks={cameraTracks}
            screenTracks={screenTracks}
            waiting={waiting}
            controls={controls}
            secs={secs}
            timerStarted={timerStarted}
            recording={recording}
            audioOnly={audioOnly}
            boardActive={boardInStage}
            onShowBoard={() => setPanelTab("whiteboard")}
            onHideBoard={() => setPanelTab((cur) => (cur === "whiteboard" ? null : cur))}
            whiteboard={whiteboard}
          />
        ) : (
          <PresenceLayout
            participants={participants}
            cameraTracks={cameraTracks}
            mode={presenceMode}
            onMode={setPresenceMode}
            waiting={waiting}
            targetName={targetName}
            controls={controls}
          />
        )}
      </div>

      {/* Tabbed side panel (Chat / Notes / Whiteboard) — right slide-over. In
          Workspace the whiteboard is routed to the stage, so it's excluded here. */}
      <div className="h-full overflow-hidden transition-[width] duration-[250ms] shrink-0" style={{ width: slideOpen ? slideWidth : 0 }}>
        {slideOpen && (
          <SidePanel
            tab={panelTab}
            onTab={setPanelTab}
            onClose={() => setPanelTab(null)}
            myName={myName}
            notesContent={notesContent}
            onNotesChange={onNotesChange}
            notesLoading={notesLoading}
            whiteboard={whiteboard}
            wide={panelTab === "whiteboard"}
          />
        )}
      </div>

      {/* Recording saved toast */}
      {recToast && (
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 bg-[#0d0d0d] rounded-2xl px-5 py-4 min-w-[320px]"
          style={{ border:`1px solid ${NEON}40`, boxShadow:"0 8px 32px rgba(0,0,0,0.7)" }}>
          <p className="text-white font-semibold text-[13px] mb-1.5">📼 Recording saved!</p>
          <p className="text-white/40 text-[11px] mb-2.5">Link expires {new Date(recToast.expires).toLocaleDateString()}</p>
          <div className="flex gap-2">
            {/* View asks the authenticated recordings endpoint for a fresh
                presigned URL for THIS recording and opens that. The old
                button navigated to the backend download URL, which now
                needs a header a navigation cannot send.

                Copy Link is gone on purpose. What it used to copy is the
                one thing we do not want loose: a URL that opens a private
                call recording for whoever ends up holding it. */}
            <button onClick={async () => {
                try {
                  const r = await fetch(`${API_BASE}/api/calling/recordings`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                  });
                  const d   = await r.json();
                  const rec = (d.recordings || []).find(x => x.id === recToast.id);
                  if (rec?.url) window.open(rec.url, "_blank", "noopener,noreferrer");
                } catch(e) { console.error("Could not open recording:", e); }
              }} className="px-4 py-[7px] rounded-[10px] font-bold text-xs" style={{ background:NEON, color:"#000" }}>View</button>
            <button onClick={() => setRecToast(null)} className="px-3 py-[7px] rounded-[10px] text-white/30 text-xs"><X size={14} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────
export default function VideoCallModal({ open, onClose, onAnswered, targetUser, audioOnly = false, roomId = null }) {
  const { user }           = useContext(AuthContext);
  const [token,  setToken]  = useState(null);
  const [error,  setError]  = useState(null);
  const [secs,   setSecs]   = useState(0);
  const [timerStarted, setTimerStarted] = useState(false);
  const [variant, setVariant] = useState("presence");
  const fetchedRoomRef  = useRef(null);
  const timerRef        = useRef(null);
  const flushNotesRef      = useRef(null);   // CallBody registers its notes flush here
  const flushWhiteboardRef = useRef(null);   // …and its whiteboard snapshot flush

  const myName     = user?.full_name || user?.name || "You";
  const myId       = String(user?.id || "");
  const targetName = targetUser?.full_name || targetUser?.name || "";

  // The single close path: flush unsaved shared-notes edits + snapshot the
  // whiteboard (both best-effort, non-blocking) BEFORE the existing teardown
  // (stop timer + onClose(secs)).
  const endCall = (s) => {
    try { flushNotesRef.current?.(); } catch {}
    try { flushWhiteboardRef.current?.(); } catch {}
    clearInterval(timerRef.current);
    onClose?.(s);
  };

  // Persisted per-user layout choice (kwery.callui.variant.<userId>, default
  // presence) — same read/validate/write idiom as RightRail's card persistence.
  useEffect(() => {
    try {
      const v = localStorage.getItem(`kwery.callui.variant.${myId}`);
      if (v === "presence" || v === "workspace") setVariant(v);
    } catch {}
  }, [myId]);
  const changeVariant = (v) => {
    setVariant(v);
    try { localStorage.setItem(`kwery.callui.variant.${myId}`, v); } catch {}
  };

  // Fetch token once per roomId
  useEffect(() => {
    if (!open || !roomId) return;
    if (fetchedRoomRef.current === roomId) return;
    fetchedRoomRef.current = roomId;
    setToken(null); setError(null); setSecs(0); setTimerStarted(false);
    fetch(`${API_BASE}/api/calling/livekit-token`, {
      method:"POST",
      headers:{ "Content-Type":"application/json", Authorization:`Bearer ${localStorage.getItem("token")}` },
      body: JSON.stringify({ room_name: roomId, participant_name: myName }),
    })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => setToken(d.token))
      .catch(e => setError(`Could not connect (${e})`));
  }, [open, roomId, myName]);

  // Timer — only starts when onTimerStart is called from the body (when 2nd
  // participant joins).
  const handleTimerStart = React.useCallback(() => {
    if (timerStarted) return;
    setTimerStarted(true);
    timerRef.current = setInterval(() => setSecs(s => s+1), 1000);
    // Second participant joined → the call connected. Tell the provider so it
    // cancels the caller's 30s "missed" timer. The CallLog status is advanced
    // to 'answered' by the callee's accept (POST /api/calling/accept), and the
    // duration is written once on close (provider.closeCall) — no per-modal
    // logging here anymore (removes the old duplicate answer+close rows).
    onAnswered?.();
  }, [timerStarted, onAnswered]);

  // Reset local media/timer state whenever the modal closes. Duration is
  // reported by the provider's onClose(secs) handler, not here.
  useEffect(() => {
    if (!open) {
      fetchedRoomRef.current = null;
      setToken(null); setError(null); setSecs(0); setTimerStarted(false);
      clearInterval(timerRef.current);
    }
  }, [open]);

  // Never return null — keep elements in DOM
  // so getUserMedia refs survive open/close

  return (
    <div style={{ display: open ? "contents" : "none" }}>
    <div className="fixed inset-0 flex flex-col" style={{ zIndex:10080, background:"#050505", fontFamily:BODY }}>

      {/* ── Header ── */}
      <div className="flex items-center justify-between px-5 py-3 shrink-0 border-b border-[#00FF99]/10"
        style={{ background:"rgba(0,0,0,0.6)", backdropFilter:"blur(20px)" }}>

        {/* Left — KweryAI brand + call info */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-[22px] h-[22px] rounded-full grid place-items-center text-[10px] font-black text-black shrink-0"
              style={{ background:`linear-gradient(135deg,${NEON},#00BFFF)` }}>K</div>
            <span className="text-white/30 text-[10px] font-semibold tracking-[0.1em]">KWERY AI</span>
          </div>

          <div className="w-px h-5 bg-white/10" />

          {/* Status dot */}
          <div className="relative">
            <div className="w-2 h-2 rounded-full" style={{ background: timerStarted ? NEON : (token ? "#facc15" : "#666"), boxShadow: timerStarted ? `0 0 8px ${NEON}` : "none" }} />
            {timerStarted && <div className="absolute rounded-full" style={{ inset:-3, border:`1px solid ${NEON}30`, animation:"kwPulse 2s ease-in-out infinite" }} />}
          </div>

          {/* Call type + name */}
          <div>
            <div className="text-white font-semibold text-[13px] flex items-center gap-1.5" style={{ letterSpacing:"-0.01em" }}>
              {audioOnly ? <Mic size={13} color={NEON} /> : <Video size={13} color={NEON} />}
              {audioOnly ? "Audio Call" : "Video Call"}{targetName ? ` · ${targetName}` : ""}
            </div>
            <div className="text-[11px] font-medium tabular-nums" style={{ color: timerStarted ? NEON : "rgba(255,255,255,0.3)", letterSpacing:"0.05em" }}>
              {timerStarted ? fmt(secs) : token ? "Waiting for others…" : "Connecting…"}
            </div>
          </div>
        </div>

        {/* Center/right — variant toggle + end call */}
        <div className="flex items-center gap-3">
          <div className="flex gap-1 bg-[#0d0d0d]/90 border border-white/10 rounded-full p-1">
            <button onClick={() => changeVariant("presence")} title="Presence view"
              className={`flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full transition-colors ${variant==="presence" ? "bg-[#00FF99] text-black" : "text-white/55 hover:text-white/80"}`}>
              <Radio size={13} /> Presence
            </button>
            <button onClick={() => changeVariant("workspace")} title="Workspace view"
              className={`flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full transition-colors ${variant==="workspace" ? "bg-[#00FF99] text-black" : "text-white/55 hover:text-white/80"}`}>
              <PanelLeft size={13} /> Workspace
            </button>
          </div>

          <button onClick={() => endCall(secs)}
            className="flex items-center gap-2 px-4 h-9 rounded-full text-white text-[13px] font-bold"
            style={{ background:"linear-gradient(135deg,#dc2626,#ef4444)", border:"1px solid rgba(239,68,68,0.4)", boxShadow:"0 4px 20px rgba(239,68,68,0.3)" }}>
            <PhoneOff size={15} /> End Call
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 min-h-0 relative overflow-hidden">

        {/* Connecting */}
        {!error && !token && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-5" style={{ background:"#050505" }}>
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 rounded-full" style={{ border:`2px solid ${NEON}20`, animation:"kwRing 1.8s ease-in-out infinite" }} />
              <div className="absolute rounded-full" style={{ inset:8, border:`2px solid ${NEON}40`, animation:"kwRing 1.8s ease-in-out infinite 0.3s" }} />
              <div className="absolute rounded-full" style={{ inset:16, border:`2px solid ${NEON}`, borderTopColor:"transparent", animation:"kwSpin 0.9s linear infinite" }} />
              <div className="absolute inset-4 grid place-items-center">
                {audioOnly ? <Mic size={22} color={NEON} /> : <Video size={22} color={NEON} />}
              </div>
            </div>
            <div>
              <p className="text-white/90 text-base font-semibold text-center mb-1">Connecting{targetName ? ` to ${targetName}` : ""}…</p>
              <p className="text-white/30 text-xs text-center">Establishing secure connection</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4" style={{ background:"#050505" }}>
            <div className="w-14 h-14 rounded-full grid place-items-center" style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)" }}>
              <AlertTriangle size={24} color="#f87171" />
            </div>
            <p className="text-[#f87171] text-sm font-medium">{error}</p>
            <button onClick={() => endCall(secs)} className="px-6 py-2.5 rounded-full font-bold text-[13px]" style={{ background:NEON, color:"#000" }}>Close</button>
          </div>
        )}

        {/* LiveKit room */}
        {!error && token && (
          <LiveKitRoom key={roomId} serverUrl={LK_URL} token={token} connect={true} video={!audioOnly} audio={true}
            options={{ rtcConfig:{ iceTransportPolicy:"all" } }}
            onDisconnected={reason => { if(reason !== 2){ endCall(secs); } }}
            style={{ height:"100%", background:"#050505" }}>
            <CallBody
              myName={myName}
              myId={myId}
              roomId={roomId}
              participantIds={[myId]}
              participantNames={[myName]}
              onTimerStart={handleTimerStart}
              timerStarted={timerStarted}
              variant={variant}
              audioOnly={audioOnly}
              targetName={targetName}
              secs={secs}
              flushNotesRef={flushNotesRef}
              flushWhiteboardRef={flushWhiteboardRef}
            />
            <RoomAudioRenderer />
          </LiveKitRoom>
        )}
      </div>

      <style>{`
        @keyframes kwSpin  { to { transform:rotate(360deg); } }
        @keyframes kwPulse { 0%,100%{opacity:0.4;transform:scale(1);}50%{opacity:0;transform:scale(1.8);} }
        @keyframes kwRing  { 0%,100%{opacity:0.3;transform:scale(0.95);}50%{opacity:0.8;transform:scale(1.05);} }
        @keyframes kwOrb   { 0%{transform:scale(.92);opacity:0;}25%{opacity:1;}100%{transform:scale(1.06);opacity:0;} }
        .lk-room-container{background:#050505!important;}
        .lk-participant-name{background:rgba(0,0,0,0.6)!important;border-radius:8px!important;padding:2px 8px!important;font-size:11px!important;color:rgba(255,255,255,0.8)!important;}
        .lk-tile{border-radius:16px!important;overflow:hidden!important;}
        .lk-grid-layout,.lk-focus-layout{gap:8px!important;padding:8px!important;}
        .lk-participant-tile{border-radius:16px!important;overflow:hidden!important;width:100%!important;height:100%!important;}
        /* Equal tiles: every video fills its box + is cropped (cover), so a tile's
           size never depends on the source track's native resolution. */
        .lk-participant-media-video{width:100%!important;height:100%!important;object-fit:cover!important;}
      `}</style>
    </div>
    </div>
  );
}
